import { useState } from "react";
import type {
  LoadBreakdownData,
  LoadOpenItemsData,
  LoadSizeMixData,
  TeamAssigneeRow,
  TeamAssigneesData,
  TeamLoadKpiSummary
} from "@silph-scope/shared";
import { useDashboard, usePanel } from "../api/hooks";
import { usePresentScale } from "../hooks/usePresentMode";
import { useTheme } from "../theme/ThemeContext";
import { ChartPanel } from "../components/ChartPanel";
import { closedBySizeOption, loadBreakdownOption, loadSizeMixOption } from "../theme/chartOptions";
import { KpiCell, LoadOpenItemsTable, LoadPageHeader } from "./load/LoadShared";

const DASHBOARD_ID = "team-load";

export function TeamLoadPage() {
  const { theme } = useTheme();
  const presentScale = usePresentScale();
  const { data: dashboard } = useDashboard(DASHBOARD_ID);
  const interval = dashboard?.refreshInterval ?? "1h";

  const kpi = usePanel<TeamLoadKpiSummary>(DASHBOARD_ID, "kpi-summary", interval);
  const breakdown = usePanel<LoadBreakdownData>(DASHBOARD_ID, "open-by-assignee", interval);
  const closedBySize = usePanel<LoadSizeMixData>(DASHBOARD_ID, "closed-by-size", interval);
  const sizeMix = usePanel<LoadSizeMixData>(DASHBOARD_ID, "size-by-assignee", interval);
  const assignees = usePanel<TeamAssigneesData>(DASHBOARD_ID, "assignees", interval);
  const openItems = usePanel<LoadOpenItemsData>(DASHBOARD_ID, "open-items", interval);

  const [selected, setSelected] = useState<string | null>(null);
  const syncedAt = kpi.data?.syncedAt ? new Date(kpi.data.syncedAt) : null;
  const isStale = kpi.data?.status === "stale" || kpi.data?.status === "failed";

  return (
    <div className="task-ops">
      <LoadPageHeader
        dashboardId={DASHBOARD_ID}
        eyebrow="People"
        title="Team Load"
        subtitle="Overdue plus work due in the next 30 days. Closures are the last 12 hours, scored as size points per day (XS 1 through XXXL 21)."
        badges={["Admin only", "Next 30 days", "Last 12 hours"]}
        syncedAt={syncedAt}
        interval={interval}
        isFetching={kpi.isFetching}
      />

      {isStale && (
        <div className="stale-banner">
          <i className="ri-error-warning-fill" />
          Data may be out of date — last sync check failed or is stale.
        </div>
      )}

      {kpi.data && <TeamKpiRow data={kpi.data.data} />}

      <section className="grid-2">
        <ChartPanel
          title="Open load by assignee"
          subtitle="Overdue and due in the next 30 days"
          option={breakdown.data ? loadBreakdownOption(theme, breakdown.data.data, presentScale) : null}
        />
        <ChartPanel
          title="Closed by size per day"
          subtitle="Size-weighted points closed in the last 12 hours, as a daily rate"
          option={closedBySize.data ? closedBySizeOption(theme, closedBySize.data.data, presentScale) : null}
        />
      </section>

      <ChartPanel
        title="Open size mix by assignee"
        subtitle="Upcoming 30-day window"
        option={sizeMix.data ? loadSizeMixOption(theme, sizeMix.data.data, presentScale) : null}
      />

      <AssigneesTable data={assignees.data?.data} selected={selected} onSelect={setSelected} />
      <LoadOpenItemsTable
        data={openItems.data?.data}
        selected={selected}
        selectedKey="assignee"
        onClear={() => setSelected(null)}
      />
    </div>
  );
}

function TeamKpiRow({ data }: { data: TeamLoadKpiSummary }) {
  return (
    <section className="dt-kpi-row">
      <KpiCell label="Assignees" value={data.assignees} />
      <KpiCell label="Open tasks" value={data.openTasks} sub="next 30d" />
      <KpiCell label="Backlog" value={data.backlog} sub="not overdue" />
      <KpiCell label="Overdue" value={data.overdue} valueClassName="is-bad" />
      <KpiCell label="Blocked" value={data.blocked} valueClassName="is-bad" />
      <KpiCell label="Open points" value={data.openPoints} sub="size weighted" />
      <KpiCell label="Closed" value={data.closedLast12h} sub="last 12h" valueClassName="is-ok" />
      <KpiCell label="Pts / day" value={data.closedPointsPerDay} sub={`${data.closedPoints} pts / 12h`} />
      <KpiCell label="Avg days to close" value={data.avgDaysToClose} />
    </section>
  );
}

function AssigneesTable({
  data,
  selected,
  onSelect
}: {
  data?: TeamAssigneesData;
  selected: string | null;
  onSelect: (assignee: string | null) => void;
}) {
  const rows = data?.rows ?? [];
  return (
    <div className="card table-panel">
      <div className="panel-header">
        <h2>Assignees</h2>
        <span className="dt-filter-note">{rows.length} people · click a row to filter open items</span>
      </div>
      <div className="dt-detail-scroll">
        <div className="tl-assignee-header">
          <span>Assignee</span>
          <span style={{ textAlign: "right" }}>Open</span>
          <span style={{ textAlign: "right" }}>Backlog</span>
          <span style={{ textAlign: "right" }}>Overdue</span>
          <span style={{ textAlign: "right" }}>Blocked</span>
          <span style={{ textAlign: "right" }}>Points</span>
          <span style={{ textAlign: "right" }}>Closed</span>
          <span style={{ textAlign: "right" }}>Pts/day</span>
          <span style={{ textAlign: "right" }}>Avg days</span>
          <span>Top type</span>
        </div>
        <div className="table-body">
          {rows.map((row) => (
            <AssigneeRow
              key={row.assignee}
              row={row}
              selected={selected === row.assignee}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AssigneeRow({
  row,
  selected,
  onSelect
}: {
  row: TeamAssigneeRow;
  selected: boolean;
  onSelect: (assignee: string | null) => void;
}) {
  return (
    <div
      className={`tl-assignee-row${selected ? " is-selected" : ""}`}
      onClick={() => onSelect(selected ? null : row.assignee)}
    >
      <span className="task-title">{row.assignee}</span>
      <span className="num">{row.open}</span>
      <span className="num">{row.backlog}</span>
      <span className={`num${row.overdue > 0 ? " is-bad" : ""}`}>{row.overdue}</span>
      <span className={`num${row.blocked > 0 ? " is-bad" : ""}`}>{row.blocked}</span>
      <span className="num">{row.openPoints}</span>
      <span className="num">{row.closed}</span>
      <span className="num">{row.closedPointsPerDay}</span>
      <span className="num">{row.avgDaysToClose ?? "—"}</span>
      <span>{row.topType}</span>
    </div>
  );
}
